import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import Toggle from '../../components/atoms/Toggle';
import AutoCompleteField from '../../components/molecules/AutoCompleteField';
import Icon, { IconSizes, IconTypes } from '../../components/foundations/Icon';
import { FORM_TRANSLATIONS, HOOK_DURATION_LIMITS, getHookDurationOptions } from '../consts';
import css from './HookAnalysisSection.css';

const HookAnalysisSection = ({ enabled, duration, onToggle, onDurationChange, translate }) => {
    const durationOptions = useMemo(() => getHookDurationOptions(translate), [translate]);
    const matchedDuration = durationOptions.find(option => option.value === duration);
    const defaultDuration =
        durationOptions.find(option => option.value === HOOK_DURATION_LIMITS.DEFAULT) || durationOptions[0];
    const selectedDuration = matchedDuration || defaultDuration;
    const selectedDurationValue = selectedDuration.value;

    // If the stored duration no longer matches any option (e.g. a value removed from
    // HOOK_DURATION_OPTIONS), sync the parent state to the fallback so Save reflects reality.
    useEffect(() => {
        if (!matchedDuration && duration !== selectedDurationValue) {
            onDurationChange(selectedDurationValue);
        }
    }, [matchedDuration, duration, selectedDurationValue, onDurationChange]);

    const handleToggle = event => onToggle(event.target.checked);
    const handleDurationChange = option => option && onDurationChange(option.value);

    return (
        <div className={classNames(css.container, { [css.enabled]: enabled })}>
            <div className={css.header}>
                <div className={css.headerText}>
                    <div className={css.title}>
                        <Icon
                            name="play-circle-line"
                            type={IconTypes.MINGCUTE}
                            size={IconSizes.MEDIUM}
                            className={css.titleIcon}
                            interactive={false}
                        />
                        <Translate id={FORM_TRANSLATIONS.HOOK_TITLE} />
                    </div>
                    <div className={css.subtitle}>
                        <Translate id={FORM_TRANSLATIONS.HOOK_SUBTITLE} />
                    </div>
                </div>
                <div className={css.toggleWrapper}>
                    <Toggle
                        checked={enabled}
                        onToggle={handleToggle}
                        ariaLabel={translate(FORM_TRANSLATIONS.HOOK_TITLE)}
                    />
                </div>
            </div>

            {enabled && (
                <div className={css.expanded}>
                    <div className={css.durationGroup}>
                        <AutoCompleteField
                            label={FORM_TRANSLATIONS.HOOK_DURATION_LABEL}
                            options={durationOptions}
                            value={selectedDuration}
                            onChange={handleDurationChange}
                            isMulti={false}
                            controlled
                            containerClass={css.durationDropdown}
                            hideDropdownIndicator={false}
                            menuPlacement="bottom"
                            selectOptions={{
                                usePortal: true,
                                menuPosition: 'fixed',
                            }}
                        />
                    </div>
                    <div className={css.scopeCallout}>
                        <div className={css.scopeLabel}>
                            <Translate id={FORM_TRANSLATIONS.HOOK_SCOPE_LABEL} />
                        </div>
                        <Translate
                            id={FORM_TRANSLATIONS.HOOK_SCOPE_TEMPLATE}
                            data={{
                                duration: (
                                    <span className={css.scopeEmphasis}>
                                        <Translate
                                            id={FORM_TRANSLATIONS.HOOK_DURATION_SECONDS}
                                            data={{ duration: selectedDurationValue }}
                                        />
                                    </span>
                                ),
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

HookAnalysisSection.propTypes = {
    enabled: PropTypes.bool.isRequired,
    duration: PropTypes.number.isRequired,
    onToggle: PropTypes.func.isRequired,
    onDurationChange: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(HookAnalysisSection);
